/**
 * @fileoverview Search Coordinator Hook
 *
 * Bu hook, ilan arama işlemlerini koordine eder.
 * Host output senkronizasyonu, sayfalama ve sıralama işlemlerini yönetir.
 *
 * @module hooks/useSearchCoordinator
 */

import { useState, useCallback } from "react";
import { useListingSearch } from "./useListingSearch.js";
import { useHostOutputSync } from "./useHostOutputSync.js";
import { SORT_ORDERS, removeFilterKey } from "../utils";

/**
 * @typedef {Object} UseSearchCoordinatorParams
 * @property {Object} widgetState - Widget state
 * @property {Function} updateWidgetState - Widget state güncelleme fonksiyonu
 * @property {Function} setRegionPriceInfo - Bölge fiyat bilgisi setter
 * @property {Object} hostOutput - ChatGPT'den gelen tool output
 */

/**
 * @typedef {Object} UseSearchCoordinatorReturn
 * @property {Object|null} output - Arama sonucu
 * @property {Function} setOutput - Output setter
 * @property {string|null} callError - Hata mesajı
 * @property {Function} setCallError - Hata setter
 * @property {boolean} isLoadingMore - Sayfalama yükleniyor mu
 * @property {boolean} isSorting - Sıralama değişiyor mu
 * @property {string} currentSort - Mevcut sıralama
 * @property {Function} callSearch - Arama fonksiyonu
 * @property {Function} loadMore - Daha fazla yükle
 * @property {Function} handleSortChange - Sıralama değiştir
 * @property {Function} handleLoadMore - Sayfalama handler
 */

/**
 * Arama işlemlerini koordine eden hook.
 *
 * Bu hook, useListingSearch ve useHostOutputSync hook'larını
 * birleştirerek daha yüksek seviyeli bir arama koordinatörü sağlar.
 *
 * @function useSearchCoordinator
 * @param {UseSearchCoordinatorParams} params - Hook parametreleri
 * @returns {UseSearchCoordinatorReturn} Arama state ve handler'ları
 *
 * @example
 * const {
 *   output,
 *   callError,
 *   currentSort,
 *   callSearch,
 *   handleSortChange,
 *   handleLoadMore,
 * } = useSearchCoordinator({
 *   widgetState,
 *   updateWidgetState,
 *   setRegionPriceInfo,
 *   hostOutput,
 * });
 */
export function useSearchCoordinator({
  widgetState,
  updateWidgetState,
  setRegionPriceInfo,
  hostOutput,
}) {
  // Sort state
  const [currentSort, setCurrentSort] = useState(SORT_ORDERS.RELEVANCE);
  const [isSorting, setIsSorting] = useState(false);

  // Core search hook
  const {
    output,
    setOutput,
    callError,
    setCallError,
    isLoadingMore,
    callSearch,
    loadMore,
  } = useListingSearch({
    widgetState,
    updateWidgetState,
    setRegionPriceInfo,
  });

  // Sync host output to local output
  useHostOutputSync(hostOutput, setOutput);

  /**
   * Sıralama değişikliği handler'ı.
   * Yeni sıralama ile arama yapar.
   */
  const handleSortChange = useCallback(async (newSort) => {
    const query = output?.query;
    if (!query?.path || newSort === currentSort) return;

    setCurrentSort(newSort);
    setIsSorting(true);
    try {
      // Filters'tan siralama'yı çıkar (sort parametresi ayrı gönderiliyor)
      const cleanFilters = removeFilterKey(query.filters || "", "siralama");
      await callSearch({
        path: query.path,
        filters: cleanFilters,
        sort: newSort,
      });
    } finally {
      setIsSorting(false);
    }
  }, [output?.query, currentSort, callSearch]);

  /**
   * Sonraki sayfayı yükle handler'ı.
   */
  const handleLoadMore = useCallback(async () => {
    const query = output?.query;
    const page = output?.page ?? 1;
    if (!query) return;

    // Filters'tan siralama'yı çıkar
    const cleanQuery = {
      ...query,
      filters: removeFilterKey(query.filters || "", "siralama"),
    };
    await loadMore(cleanQuery, page, currentSort);
  }, [output?.query, output?.page, currentSort, loadMore]);

  return {
    // State
    output,
    setOutput,
    callError,
    setCallError,
    isLoadingMore,
    isSorting,
    currentSort,

    // Actions
    callSearch,
    loadMore,
    handleSortChange,
    handleLoadMore,
  };
}

export default useSearchCoordinator;
